import React, { useState, useMemo } from 'react';
import { SimilarOrganization } from '../../../types/interfaces';
import CompetitorCardWithAccess from '../../misc/CompetitorCardWithAccess';
import { useTranslation } from 'react-i18next';

interface SimilarOrganizationsListProps {
  similarOrganizations: SimilarOrganization[];
}

const SimilarOrganizationsList: React.FC<SimilarOrganizationsListProps> = ({ similarOrganizations }) => {
  const { t } = useTranslation();

  const sortedOrganizations = useMemo(() => {
    return [...similarOrganizations].sort((a, b) => b.similarity - a.similarity);
  }, [similarOrganizations]);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const dataPerPage = 6;

  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;
  const currentData = sortedOrganizations.slice(indexOfFirstData, indexOfLastData);

  const totalPages = Math.ceil(sortedOrganizations.length / dataPerPage);
  const goToNextPage = () => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const goToPreviousPage = () => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev));

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      <div className="flex items-center mb-6">
        <div className="w-3 h-10 bg-gray-800 mr-4"></div>
        <h3 className="text-3xl md:text-4xl font-bold">{t('similarOrganizations.title')}</h3>
      </div>

      {sortedOrganizations.length === 0 ? (
        <p className="text-center">{t('similarOrganizations.no_organizations')}</p>
      ) : (
        <>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentData.map((org, index) => {
              const competitorData = {
                id: org.organization_id || '',
                name: org.name || '',
                similarity: org.similarity,
                clean_description: org.clean_description,
                logo_url: org.logo_url,
                phone: org.phone,
                founded_year: org.founded_year,
                estimated_num_employees: org.estimated_num_employees,
                street_address: org.street_address,
                city: org.city,
                state: org.state,
                country: org.country,
                postal_code: org.postal_code,
                website_url: org.website_url,
                linkedin_url: org.linkedin_url,
                twitter_url: org.twitter_url,
                facebook_url: org.facebook_url,
              };

              return <CompetitorCardWithAccess key={index} competitor={competitorData} />;
            })}
          </ul>

          <div className="mt-6 flex flex-col items-center">
            <div className="flex justify-center items-center space-x-4 w-full">
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
              >
                {t('similarOrganizations.previous')}
              </button>
              <span>
                {t('similarOrganizations.pagination', { current: currentPage, total: totalPages })}
              </span>
              <button
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
              >
                {t('similarOrganizations.next')}
              </button>
            </div>

            <div className="mt-3 text-center text-sm text-gray-600">
              {t('similarOrganizations.footer_text')}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SimilarOrganizationsList;
